import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Check,
  X,
  Sparkle,
  Crown,
  Star,
  ArrowRight,
  Headset,
  ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/razorpay";

const PLANS = [
  {
    id: "free",
    name: "Free",
    tagline: "Curiosity-led shoppers",
    price: 0,
    period: "forever",
    cta: "Start free",
    accent: "border-[#E8DFD1]",
    badge: null,
    paid: false,
    features: [
      { ok: true, text: "5 AI chat questions per day" },
      { ok: true, text: "3 hallmark verifications per day" },
      { ok: true, text: "Live city-wise board rates (6 cities)" },
      { ok: true, text: "Price calculator with full breakup" },
      { ok: true, text: "Read full Mine AI Journal" },
      { ok: false, text: "Style Match recommendations" },
      { ok: false, text: "Resell Valuator with photo AI" },
      { ok: false, text: "Price alerts via email" },
    ],
  },
  {
    id: "plus",
    name: "Plus",
    tagline: "Smart family buyers",
    price: 199,
    period: "/month",
    cta: "Upgrade to Plus",
    accent: "border-[#3B5BDB] ring-2 ring-[#3B5BDB] ring-offset-2",
    badge: "Most popular",
    paid: true,
    features: [
      { ok: true, text: "Unlimited AI chat questions" },
      { ok: true, text: "Unlimited hallmark verification" },
      { ok: true, text: "Style Match — 6 personalised picks" },
      { ok: true, text: "Resell Valuator with Gemini Vision" },
      { ok: true, text: "Up to 5 active price alerts (email)" },
      { ok: true, text: "Multi-language: Tamil / Hindi / Telugu" },
      { ok: true, text: "Save and revisit past chats" },
      { ok: false, text: "Priority human review" },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Serious investors & wedding budgets",
    price: 499,
    period: "/month",
    cta: "Upgrade to Pro",
    accent: "border-[#D4AF37]/60",
    badge: "Concierge",
    paid: true,
    features: [
      { ok: true, text: "Everything in Plus" },
      { ok: true, text: "Unlimited price alerts (email + SMS)" },
      { ok: true, text: "Weekly board-rate trend reports" },
      { ok: true, text: "Deep purchase analysis (PDF takeaway)" },
      { ok: true, text: "Priority human jeweller review on big buys" },
      { ok: true, text: "Early access to new Mine AI features" },
      { ok: true, text: "Concierge WhatsApp support" },
      { ok: true, text: "Auromine network shop discounts" },
    ],
  },
];

const FAQ = [
  {
    q: "Why pay for Mine AI when I can ask a jeweller?",
    a: "Because Mine AI sides with your wallet, not the shop. Jewellers earn from making charges and stone markups — Mine AI never earns a rupee from any sale.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Plans are month-to-month with no lock-in. Cancel from Dashboard → My Plan and you keep access until the period ends.",
  },
  {
    q: "Will Plus or Pro ever go up in price?",
    a: "Early subscribers are price-locked. Whatever rate you join at is the rate you keep, even when public pricing changes.",
  },
  {
    q: "Are there discounts for retailers or jewellers?",
    a: "Yes — we have a B2B Retailer plan with bulk seats and a widget to embed Mine AI on your site. Contact us via the Talk to Us button on Pro.",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ razorpay_enabled: false });
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api
      .get("/billing/plans")
      .then((r) => setMeta({ razorpay_enabled: r.data.razorpay_enabled }))
      .catch(() => {});
  }, []);

  const handleUpgrade = async (plan) => {
    if (plan.id === "free") {
      navigate(user ? "/dashboard" : "/signup");
      return;
    }
    if (!user) {
      navigate(`/login?next=/pricing`);
      return;
    }
    if (!meta.razorpay_enabled) {
      // Soft fallback: capture interest while keys aren't configured.
      try {
        await api.post("/waitlist", {
          name: user.name || `${plan.name} interest`,
          email: user.email,
          phone: user.phone || "+910000000000",
          user_type: `${plan.id}_waitlist`,
          city: "",
        });
        toast.success(`You're on the ${plan.name} waitlist! We'll be in touch the moment payments go live.`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not save");
      }
      return;
    }
    setBusy(plan.id);
    try {
      const result = await startCheckout(plan.id, {
        name: user.name,
        email: user.email,
        contact: user.phone,
      });
      toast.success(`Welcome to ${plan.name}! Your plan is active until ${new Date(result.plan_active_until).toLocaleDateString()}.`);
      navigate("/dashboard?tab=plan");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Payment failed";
      if (msg !== "Payment cancelled") toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="pricing-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-[#D4AF37]" /> Pricing
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#0A1128] tracking-tight max-w-3xl leading-[1.05]">
          Honest pricing for your{" "}
          <span className="gold-text">honest jewellery advisor</span>.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-[#4A5568] max-w-2xl">
          Start free. Upgrade when Mine AI saves you more than it costs — usually after the first ₹50,000 purchase.
        </p>
        {!meta.razorpay_enabled && (
          <p className="mt-3 inline-block text-[11px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#8a6d1c]" data-testid="pricing-coming-soon">
            Test mode payments coming online — tap upgrade to reserve your spot.
          </p>
        )}
      </section>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-12">
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-7">
          {PLANS.map((p, i) => (
            <PlanCard
              key={p.id}
              plan={p}
              delay={i * 80}
              onClick={() => handleUpgrade(p)}
              busy={busy === p.id}
              currentPlan={user?.plan}
            />
          ))}
        </div>

        {/* Trust strip */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          <TrustItem
            icon={ShieldCheck}
            title="No-spam guarantee"
            text="Your data is yours. We will never sell it."
          />
          <TrustItem
            icon={Headset}
            title="Real humans"
            text="Auromine, Chennai. Reach the team via WhatsApp on Pro."
          />
          <TrustItem
            icon={Star}
            title="Built in India"
            text="Made for Indian gold buying — Tamil/Hindi/Telugu/English."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
          Frequently asked
        </p>
        <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#0A1128]">
          Quick answers
        </h2>
        <div className="mt-8 space-y-3">
          {FAQ.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="mc-card p-8 sm:p-10 text-center bg-gradient-to-br from-[#3B5BDB]/5 to-[#D4AF37]/5">
          <h3 className="font-display text-3xl sm:text-4xl text-[#0A1128]">
            Try Mine AI free today
          </h3>
          <p className="mt-2 text-[#4A5568] max-w-xl mx-auto">
            No card needed. Start with 5 questions and see if it saves you more than it asks for.
          </p>
          <Link
            to="/signup"
            data-testid="bottom-cta-signup"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth"
          >
            Create free account <ArrowRight size={16} weight="bold" />
          </Link>
          <p className="mt-4 text-xs text-[#4A5568]/80">
            By subscribing, you agree to our{" "}
            <Link to="/terms" className="text-[#3B5BDB] underline">Terms</Link>,{" "}
            <Link to="/refund-policy" className="text-[#3B5BDB] underline">Refund Policy</Link>, and{" "}
            <Link to="/privacy" className="text-[#3B5BDB] underline">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}

function PlanCard({ plan, delay, onClick, busy, currentPlan }) {
  const isCurrent = currentPlan && currentPlan === plan.id;
  return (
    <div
      className={`mc-card p-7 lift smooth fade-up flex flex-col relative ${plan.accent}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`plan-${plan.id}`}
    >
      {plan.badge && (
        <span
          className={`absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold ${
            plan.id === "plus"
              ? "bg-[#3B5BDB] text-white"
              : "bg-[#D4AF37] text-white"
          }`}
          data-testid={`plan-${plan.id}-badge`}
        >
          {plan.badge}
        </span>
      )}

      <div className="flex items-center gap-2">
        {plan.id === "pro" ? (
          <Crown size={20} weight="duotone" className="text-[#D4AF37]" />
        ) : plan.id === "plus" ? (
          <Sparkle size={20} weight="duotone" className="text-[#3B5BDB]" />
        ) : (
          <Star size={20} weight="duotone" className="text-[#4A5568]" />
        )}
        <h3 className="font-display text-3xl text-[#0A1128]">{plan.name}</h3>
      </div>
      <p className="text-xs text-[#4A5568] mt-1">{plan.tagline}</p>

      <div className="mt-6 flex items-baseline gap-1">
        {plan.price === 0 ? (
          <span className="font-display text-5xl text-[#0A1128]">Free</span>
        ) : (
          <>
            <span className="font-display text-5xl text-[#0A1128] tabular-nums">
              ₹{plan.price}
            </span>
            <span className="text-sm text-[#4A5568]">{plan.period}</span>
          </>
        )}
      </div>
      {plan.price === 0 && (
        <p className="text-xs text-[#4A5568] mt-1">{plan.period}</p>
      )}

      <button
        data-testid={`plan-${plan.id}-cta`}
        onClick={onClick}
        disabled={busy || isCurrent}
        className={`mt-6 w-full py-3 rounded-md font-medium smooth disabled:opacity-60 disabled:cursor-not-allowed ${
          plan.id === "plus"
            ? "bg-[#3B5BDB] text-white hover:bg-[#2F49AF]"
            : plan.id === "pro"
              ? "bg-[#0A1128] text-white hover:bg-[#0A1128]/90"
              : "border border-[#0A1128] text-[#0A1128] hover:bg-[#0A1128] hover:text-white"
        }`}
      >
        {isCurrent ? "Current plan" : busy ? "Opening checkout..." : plan.cta}
      </button>

      <ul className="mt-7 space-y-2.5">
        {plan.features.map((f, i) => (
          <li
            key={i}
            className={`flex items-start gap-2.5 text-sm ${
              f.ok ? "text-[#0A1128]" : "text-[#4A5568]/60 line-through decoration-1"
            }`}
            data-testid={`plan-${plan.id}-feat-${i}`}
          >
            {f.ok ? (
              <Check
                size={16}
                weight="bold"
                className={`shrink-0 mt-0.5 ${
                  plan.id === "pro" ? "text-[#D4AF37]" : "text-[#16A34A]"
                }`}
              />
            ) : (
              <X size={16} weight="bold" className="shrink-0 mt-0.5 text-[#4A5568]/50" />
            )}
            <span>{f.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrustItem({ icon: Icon, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={22} weight="duotone" className="text-[#3B5BDB] shrink-0 mt-0.5" />
      <div>
        <p className="font-medium text-sm text-[#0A1128]">{title}</p>
        <p className="text-xs text-[#4A5568] mt-0.5">{text}</p>
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="mc-card p-5 cursor-pointer smooth"
      onClick={() => setOpen(!open)}
      data-testid="faq-item"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-lg text-[#0A1128]">{q}</p>
        <span
          className={`text-[#3B5BDB] text-xl transition-transform ${
            open ? "rotate-45" : ""
          }`}
          aria-hidden
        >
          +
        </span>
      </div>
      {open && <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{a}</p>}
    </div>
  );
}
